import {BillApiDto, UpdateBillApiDto} from "../../api/model/rest";
import AuthenticatedEntity from "../user/AuthenticatedEntity";
import BillItem from "./BillItem";
import {none, option, Option} from "ts-option";
import DateUtils from "../../utils/DateUtils";
import moment = require("moment");

export default class Bill {

	constructor(public readonly id: string,
		public readonly creationDateTime: moment.Moment,
		public readonly creator: AuthenticatedEntity,
		public readonly billDate: moment.Moment,
		public readonly dueDate: Option<moment.Moment>,
		public readonly invoiceId: string,
		public readonly jobId: Option<string>,
		public readonly items: BillItem[],
		public readonly partnerBrandId: string,
		public readonly referenceNumber: string,
		public readonly status: string,
		public readonly subtotal: number,
		public readonly tax: Option<number>,
		public readonly total: number,
		public readonly payViaMobile: boolean,
		public readonly sent: boolean) {}

	static fromJson(json: BillApiDto): Bill {
		const creationDateTime = json.creationDateTime ? moment(json.creationDateTime) : null;
		const creator = AuthenticatedEntity.fromJson(json.creator);
		const dueDate = json.dueDate ? option(moment(json.dueDate, DateUtils.ISO_DATE_FORMAT)) : none;
		const billDate = moment(json.billDate, DateUtils.ISO_DATE_FORMAT);
		const items = json.items.map(BillItem.fromJson);
		return new Bill(json.id, creationDateTime, creator, billDate, dueDate, json.invoiceId, option(json.jobId), items,
			json.partnerBrandId, json.referenceNumber, json.status, json.subtotal, option(json.tax), json.total,
			json.payViaMobile, json.sent);
	}

	toJson(): UpdateBillApiDto {
		return {
			billDate: this.billDate.format(DateUtils.ISO_DATE_FORMAT),
			dueDate: this.dueDate.map(bd => bd.format(DateUtils.ISO_DATE_FORMAT)).orNull
		}
	}

	/**
	 * Get bill status label.
	 * @returns {string}
	 */
	getStatusLabel(): string {
		return this.status.replaceUnderscoresWithSpaces();
	}

	/**
	 * If true, total value of bill is greater than zero.
	 */
	hasValue(): boolean {
		return this.total > 0;
	}

	isPaid(): boolean {
		return this.status !== BillStatus.NEW;
	}

	copy({payViaMobile = this.payViaMobile, billDate = this.billDate, dueDate = this.dueDate}) {
		return new Bill(this.id, this.creationDateTime, this.creator, billDate, dueDate, this.invoiceId, this.jobId, this.items,
			this.partnerBrandId, this.referenceNumber, this.status, this.subtotal, this.tax, this.total, payViaMobile, this.sent);
	}

	public static compare(a: Bill, b: Bill) {
		return a.creationDateTime.isBefore(b.creationDateTime) ? 1 : -1;
	}
}

export enum BillStatus {
	NEW = "NEW",
	PARTIALLY_PAID = "PARTIALLY_PAID",
	PAID_NEEDS_CONFIRMATION = "PAID_NEEDS_CONFIRMATION",
	PAID_FUNDS_PENDING = "PAID_FUNDS_PENDING",
	PAID = "PAID"
}
